import { useMemo, useState } from 'react';

import type { WebhookSubscriptionsQuery } from '#src/types/generated/graphql.ts';
import { SmallBoldUnderlinedTextDropdown } from '../SmallBoldUnderlinedTextDropdown/SmallBoldUnderlinedTextDropdown.tsx';

type PaginationDirection = 'BACKWARDS' | 'FORWARDS';

type PageInfo = WebhookSubscriptionsQuery['webhookSubscriptions']['pageInfo'];

type ForwardPageInfo = Pick<PageInfo, 'hasNextPage' | 'endCursor'>;

const isFullPageInfo = (
  pageInfo: ForwardPageInfo | PageInfo,
): pageInfo is PageInfo => pageInfo.hasOwnProperty('hasNextPage');

export type ProcessPageInfo = (pageInfo: ForwardPageInfo | PageInfo) => {
  shouldLoadMore: boolean;
  variables: {
    before?: string | null;
    after?: string | null;
  };
};

export interface PaginationDirectionState {
  firstOrLast: 'first' | 'last';
  paginationDirection: PaginationDirection;

  processPageInfo: ProcessPageInfo;

  setPaginationDirection: (paginationDirection: PaginationDirection) => void;
}

export const usePaginationDirectionState = (
  initialState: PaginationDirection = 'BACKWARDS',
): PaginationDirectionState => {
  const [paginationDirection, setPaginationDirection] =
    useState<PaginationDirection>(initialState);

  return useMemo(() => {
    const firstOrLast = paginationDirection === 'FORWARDS' ? 'first' : 'last';

    const processPageInfo = (pageInfo: ForwardPageInfo | PageInfo) => {
      if (isFullPageInfo(pageInfo)) {
        return {
          shouldLoadMore: Boolean(
            paginationDirection === 'FORWARDS'
              ? pageInfo.hasNextPage
              : pageInfo.hasPreviousPage,
          ),
          variables:
            paginationDirection === 'FORWARDS'
              ? { after: pageInfo.endCursor }
              : { before: pageInfo.startCursor },
        };
      }

      return {
        shouldLoadMore: Boolean(pageInfo.hasNextPage),
        variables: { after: pageInfo.endCursor },
      };
    };

    return {
      firstOrLast,
      paginationDirection,
      processPageInfo,
      setPaginationDirection,
    };
  }, [paginationDirection, setPaginationDirection]);
};

export const PaginationDirectionPicker = ({
  paginationDirection,
  setPaginationDirection,
}: Pick<
  ReturnType<typeof usePaginationDirectionState>,
  'paginationDirection' | 'setPaginationDirection'
>) => (
  <SmallBoldUnderlinedTextDropdown<PaginationDirection>
    setValue={(value) => setPaginationDirection(value)}
    options={[
      { label: 'Newest first', value: 'BACKWARDS' },
      { label: 'Oldest first', value: 'FORWARDS' },
    ]}
    value={paginationDirection}
  />
);
